import React, { useRef, useCallback } from 'react';
import { Upload, Plus, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import { ContractWizardState, ContractEvent } from '../../types/contract';
import { normalizeCodeName } from '../../utils/schemaUtils'; // Assuming these exist

interface ContractEventsPanelProps {
  state: ContractWizardState;
  selectedEvent: ContractEvent | null;
  onEventSelect: (event: ContractEvent | null) => void;
  updateState: (updates: Partial<ContractWizardState>) => void;
  isEditable: boolean;
  onFilesUpload: (files: File[]) => void;
  onDeleteEventRequest: (event: ContractEvent) => void; // New prop for delete request
  isSmallScreen: boolean;
}

const ContractEventsPanel: React.FC<ContractEventsPanelProps> = ({
  state,
  selectedEvent,
  onEventSelect,
  updateState,
  isEditable,
  onFilesUpload,
  onDeleteEventRequest, // Destructure new prop
  isSmallScreen,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = React.useState(false); // Internal state for drag over effect

  const panelWidthClass = isSmallScreen ? "w-full md:w-1/4 lg:w-1/5 min-w-[140px] max-w-[250px]" : "w-full md:w-1/3 lg:w-1/4 min-w-[180px] lg:max-w-[400px]";

  // Internal drag and drop handlers for visual feedback
  const handleInternalDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleInternalDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleInternalDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault(); // Prevent default browser behavior
    setIsDragOver(false);
    onFilesUpload(Array.from(e.dataTransfer.files)); // Directly pass File[]
  }, [onFilesUpload]);

  const handleAddEmptyEvent = useCallback(() => {
    const newEvent: ContractEvent = {
      id: uuidv4(),
      name: 'New Event',
      codeName: normalizeCodeName('New Event'), // Add codeName
      properties: [],
    };
    const updatedEvents = [...state.events, newEvent];
    updateState({ events: updatedEvents });
    onEventSelect(newEvent); // Select the newly added event
  }, [state.events, updateState, onEventSelect]);

  // Removed handleDeleteEvent as it's now handled by parent via onDeleteEventRequest

  return (
    <div className={clsx(panelWidthClass, "bg-white dark:bg-gray-800 border-r dark:border-gray-700 p-4 md:p-6 flex flex-col gap-4 shadow-sm flex-1 min-h-0")}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".json"
        multiple
        onChange={async (e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            onFilesUpload(Array.from(files)); // Directly pass File[]
          }
        }}
      />
      <div className="flex items-center justify-between">
        {!isSmallScreen && (
          <div className="text-text-primary dark:text-white font-bold text-base md:text-lg">Events</div>
        )}
        {state.events.length > 0 && (
          <div className="flex gap-1">
            <button
              className={clsx(
                "flex items-center gap-2 px-4 py-2 text-blue-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md text-sm font-medium transition-colors",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={() => fileInputRef.current?.click()}
              disabled={!isEditable}
            >
              <Upload size={16} className="text-blue-700 dark:text-white" />
              Upload
            </button>
            <button
              className={clsx(
                "flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-100  dark:bg-blue-900/30 dark:border-blue-700/50 hover:bg-blue-200 dark:hover:bg-blue-900  shadow hover:shadow-md text-blue-800 dark:text-blue-100 rounded-md text-sm font-medium transition-colors",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={handleAddEmptyEvent}
              disabled={!isEditable}
            >
              <Plus size={16} className="text-blue-700 dark:text-white" />
              {!isSmallScreen && "Add "}Event
            </button>
          </div>
        )}
      </div>

      {state.events.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex flex-col items-center justify-center py-12 px-6 md:px-10 border-2 border-dashed rounded-lg bg-gray-50 dark:bg-gray-700 ${isDragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-gray-300 dark:border-gray-600'}`}
          onDragOver={handleInternalDragOver}
          onDragLeave={handleInternalDragLeave}
          onDrop={handleInternalDrop}
        >
          <Upload size={48} className="text-gray-400 dark:text-gray-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No events added yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-2">
            Upload JSON event schemas or add an empty event.
          </p>
          <div className="flex gap-2 mt-4">
            <button
              className={clsx(
                "flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-100  dark:bg-blue-900/30 dark:border-blue-700/50 hover:bg-blue-200 dark:hover:bg-blue-900  shadow hover:shadow-md text-blue-800 dark:text-blue-100 rounded-md text-sm font-medium transition-colors",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={() => fileInputRef.current?.click()}
              disabled={!isEditable}
            >
              <Upload size={16} className="text-blue-700 dark:text-white" />
              Upload
            </button>
            <button
              className={clsx(
                "flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg text-white rounded-md text-sm font-medium transition-colors",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={handleAddEmptyEvent}
              disabled={!isEditable}
            >
              <Plus size={16} className="text-white" />
              {!isSmallScreen && "Add "}Event
            </button>
          </div>
        </motion.div>
      ) : (
        <div
          className={`flex-1 overflow-y-auto border rounded-md ${isDragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-gray-200 dark:border-gray-700'}`}
          onDragOver={handleInternalDragOver}
          onDragLeave={handleInternalDragLeave}
          onDrop={handleInternalDrop}
        >
          {state.events.map((event: ContractEvent) => (
            <div
              key={event.id}
              className={clsx(
                `flex items-center justify-between px-5 py-4 cursor-pointer border-b dark:border-gray-700 group`,
                selectedEvent?.id === event.id ? 'bg-blue-100/60 dark:bg-blue-700/20 hover:dark:bg-blue-700/30' : 'hover:bg-gray-50 dark:hover:bg-gray-600/20'
              )}
              onClick={() => onEventSelect(event)}
            >
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <div className="font-medium text-gray-900 text-[15px] dark:text-white truncate">{event.name || 'Unnamed Event'}</div>
                {!isSmallScreen && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-blue-100 dark:bg-blue-500/10 text-blue-800 dark:text-blue-300 truncate">
                    {normalizeCodeName(event.name) || 'no_code_name'}
                  </span>
                )}
              </div>
              <button
                className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900 text-red-500 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteEventRequest(event); // Call the new prop
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractEventsPanel;
